import { SortingDirection, SortingParams } from '../types/URLParamTypes'
import { ReadableSearchParams, toSearchParams, updateQueryParamsInPath } from '../URLParam/QueryParamPathHelper'

export const ORDER_BY_QUERY_PARAM = 'orderBy'

export const DEFAULT_SORTING_DIRECTION = SortingDirection.ASCENDING

const isSortableDirection = (value: string): value is SortingDirection =>
    value === SortingDirection.ASCENDING || value === SortingDirection.DESCENDING

const isSameSorting = (sorting: SortingParams, other: SortingParams | undefined): boolean =>
    !!other && sorting.sortBy === other.sortBy && sorting.direction === other.direction

/**
 * Reads the sorting state from URL search params. The value holds the column(s) and the direction in
 * the same shape the API expects, for example `orderBy=name+asc` or a multi-column
 * `orderBy=colA+colB+desc`. Only a trailing `asc`/`desc` is treated as the direction; everything
 * before it becomes `sortBy`. A missing or unusable direction becomes ascending and a missing
 * column falls back to the provided defaults. Column names are not checked against the overview, so
 * an unknown column is passed on as it is.
 */
export const extractQueryParamsForSorting = (
    source: ReadableSearchParams | string,
    defaults: SortingParams
): SortingParams => {
    const value = toSearchParams(source).get(ORDER_BY_QUERY_PARAM)?.trim()
    if (!value) return defaults

    const parts = value.split(/\s+/).filter(Boolean)
    if (!parts.length) return defaults

    // The sorting query can contain multiple columns and a direction. We need to extract the direction from the last part.
    // If the last part is a valid direction, we use it as the direction and the rest as the sortBy.
    // If the last part is not a valid direction, we use the default direction and the parts as the sortBy.
    const maybeDirection = parts[parts.length - 1].toLowerCase()
    if (isSortableDirection(maybeDirection)) {
        const sortBy = parts.slice(0, -1).join(' ')
        if (!sortBy) return defaults
        return { sortBy, direction: maybeDirection }
    }

    return {
        sortBy: parts.join(' '),
        direction: DEFAULT_SORTING_DIRECTION,
    }
}

/**
 * Returns the path with the sorting state applied to its query string. Keeps any unrelated params
 * (search, pagination, filters) intact and leaves the param out when the sorting equals the default
 * of the overview so the URL stays clean until it is actually sorted differently.
 */
export const addSortingQueryParamsToPath = (path: string, sorting: SortingParams, defaults?: SortingParams): string =>
    updateQueryParamsInPath(path, (params) => {
        const isUnsorted = !sorting?.sortBy || sorting.direction === SortingDirection.NONE
        if (isUnsorted || isSameSorting(sorting, defaults)) {
            params.delete(ORDER_BY_QUERY_PARAM)
            return
        }
        params.set(ORDER_BY_QUERY_PARAM, `${sorting.sortBy} ${sorting.direction}`)
    })
